<template>
  <aside id="result-card">
    <div class="card">
      <div class="card-header">Votre sélection</div>
      <div class="card-body">
        <div class="plan-header">
          <h2>{{ result.plan.name }}</h2>
          <div class="offer-container">
            <span v-if="result.savingsBadge" class="savings-badge">
              {{ result.offerText }}
            </span>
            <span v-if="result.offerText" class="offer-text">
              {{ result.savingsBadge }}
            </span>
          </div>
        </div>

        <div class="price-display" v-if="result.plan.id !== 'custom'">
          <span class="price">{{ formattedPrice }}€ <sup>HT</sup></span>
          <span class="period">&nbsp;{{ result.priceLabel }}</span>
        </div>

        <div class="contact-display" v-else>
          <span class="contact-text">
            Vous avez besoin de plus de pages, d'utilisateurs ou de sources ?
          </span>
          <span class="contact-text">
            Nous pouvons vous proposer un plan personnalisé.
          </span>
        </div>

        <ul class="features-list" v-if="result.plan.id !== 'custom'">
          <li>
            Jusqu'à
            <span class="feature-value">{{
              result.displayValues.socialAccounts
            }}</span>
            pages connectées
            <span v-if="result.extraCosts.social" class="extra-cost-text">
              {{ result.extraCosts.social }}
            </span>
          </li>
          <li>
            Jusqu'à
            <span class="feature-value">{{ result.displayValues.users }}</span>
            utilisateurs
            <span v-if="result.extraCosts.users" class="extra-cost-text">
              {{ result.extraCosts.users }}
            </span>
          </li>
          <li>
            Poster depuis
            <span class="feature-value">{{
              result.displayValues.sources
            }}</span>
            sources
            <span v-if="result.extraCosts.sources" class="extra-cost-text">
              {{ result.extraCosts.sources }}
            </span>
          </li>
          <li
            v-for="(featureKey, index) in featureKeys"
            :key="index"
            :class="{ 'not-included': !result.features[featureKey] }"
          >
            {{ featureLabels[featureKey] }}
          </li>
        </ul>

        <button
          class="cta-btn"
          @click="result.plan.id === 'custom' ? openChat() : redirectToSaaS()"
        >
          {{
            result.plan.id === "custom" ? "Nous contacter" : "Choisir ce plan"
          }}
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { ENVIRONMENTS } from "@config/pricingConfig"
import { featureLabels } from "@data/plans"

export default {
  name: "ResultCard",

  props: {
    result: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value &&
          typeof value === "object" &&
          value.plan &&
          typeof value.finalPrice === "number" &&
          typeof value.priceLabel === "string" &&
          value.displayValues &&
          typeof value.features === "object"
        )
      },
    },
    environment: {
      type: String,
      required: true,
      validator: (value) => Object.values(ENVIRONMENTS).includes(value),
    },
  },

  data() {
    return {
      ENVIRONMENTS,
      featureLabels,
      featureKeys: [
        "native",
        "planification",
        "moderation",
        "reporting",
        "collaboration",
      ],
    }
  },

  computed: {
    formattedPrice() {
      return this.result.finalPrice.toLocaleString("fr-FR")
    },
  },

  methods: {
    // Redirect to SaaS with plan ID + period as query parameter
    redirectToSaaS() {
      const planId = this.result.plan.id
      const period = this.result.period
      const saasUrl = `https://saas.mashup-web.com/#/?plan=${planId}&period=${period}`
      window.location.href = saasUrl
    },

    // Open chat based on environment (HubSpot for Webflow, Crisp for SaaS)
    openChat() {
      const customMessage =
        "Bonjour, je souhaite être contacté pour une offre sur mesure."

      switch (this.environment) {
        case ENVIRONMENTS.WEBFLOW:
          if (
            window.HubSpotConversations &&
            window.HubSpotConversations.widget
          ) {
            window.HubSpotConversations.widget.open()
            // Pre-fill HubSpot chat message
            setTimeout(() => {
              const iframe = document.querySelector(
                "#hubspot-messages-iframe-container iframe"
              )
              if (iframe && iframe.contentDocument) {
                const messageField = iframe.contentDocument.querySelector(
                  '[data-test-id="widget-textarea"]'
                )
                if (messageField) {
                  messageField.value = customMessage
                  messageField.dispatchEvent(
                    new Event("input", { bubbles: true })
                  )
                  // Trigger focus to show the message
                  messageField.focus()
                }
              }
            }, 800)
          } else {
            console.warn("HubSpot chat not available")
          }
          break

        case ENVIRONMENTS.SAAS:
          if (window.$crisp) {
            window.$crisp.push(["do", "chat:open"])
            // Pre-fill Crisp chat message
            window.$crisp.push(["set", "message:text", [customMessage]])
          } else {
            console.warn("Crisp chat not available")
          }
          break

        default:
          console.error(`Unknown environment: ${this.environment}`)
      }
    },
  },
}
</script>

<style scoped>
/* RIGHT COLUMN (CARD) */
#result-card {
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.card {
  display: flex;
  flex-direction: column;
  width: 390px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(107, 43, 89, 0.15);
  border: 3px solid var(--tertiary-color);
  background: var(--tertiary-color);

  @media (max-width: 400px) {
    width: 360px;
  }
}

.card-header {
  background: var(--tertiary-color);
  color: #fff;
  text-align: center;
  padding: 15px;
  font-size: 16px;
  font-weight: 500;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 17px;
  padding: 25px;
  background: #fff;
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.plan-header h2 {
  font-size: 30px;
  font-weight: 600;
  margin: 0;
}

.offer-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.savings-badge {
  background: var(--secondary-color);
  color: #fff;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 20px;
}

.offer-text {
  color: var(--secondary-color);
  font-size: 13px;
  font-weight: 800;
}

/* Common display styles */
.price-display,
.contact-display {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 30px;
}

.price-display {
  color: var(--primary-text-color);
}

.contact-display {
  color: var(--tertiary-color);
}

.contact-text {
  font-size: 18px;
  font-weight: 500;
  color: var(--tertiary-color);
  display: block;
}

.contact-text:first-child {
  margin-bottom: 15px;
}

.price {
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-text-color);

  sup {
    font-size: 12px;
    font-weight: 600;
  }
}

.period {
  font-size: 18px;
  font-weight: 400;
  color: var(--secondary-text-color);
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
}

.features-list li {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  gap: 5px;
}

.features-list li::before {
  content: "✓";
  color: var(--tertiary-color);
  font-weight: 800;
  font-size: 16px;
  margin-right: 8px;
}

.features-list li.not-included::before {
  content: "✕";
  color: #b2b3b7;
  font-size: 12px;
}

.features-list li.not-included {
  color: #b2b3b7;
}

.feature-value {
  font-weight: 600;
}

.extra-cost-text {
  font-size: 12px;
}

.cta-btn {
  width: 100%;
  background: var(--tertiary-color);
  color: #fff;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
}

.cta-btn:hover {
  opacity: 0.9;
}
</style>
